// Recurrence expansion, modelled on Google Calendar's custom-repeat options.
// A rule looks like:
//   { freq:'daily'|'weekly'|'monthly'|'yearly',
//     interval: 1..99,
//     byDay: [0..6],                 // weekly only (0 = Sunday)
//     monthMode: 'date'|'weekday',   // monthly only: "on the 14th" vs "2nd Tuesday"
//     end: { type:'never'|'on'|'after', date:'YYYY-MM-DD', count: n } }
// Expansion is capped so a runaway "never" rule can't fill the database.

const MAX_OCCURRENCES = 400;

const daysInMonth = (y, m) => new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
const iso = (d) => d.toISOString().slice(0, 10);
const at = (y, m, d) => new Date(Date.UTC(y, m, d));

export function describeRule(r) {
  if (!r || r.freq === 'none') return 'Does not repeat';
  const n = Math.max(1, r.interval || 1);
  const DOW = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  let s;
  if (r.freq === 'daily') s = n === 1 ? 'Daily' : `Every ${n} days`;
  else if (r.freq === 'weekly') {
    const days = (r.byDay || []).slice().sort();
    const label = days.length ? ` on ${days.map((d) => SHORT[d]).join(', ')}` : '';
    s = (n === 1 ? 'Weekly' : `Every ${n} weeks`) + label;
  } else if (r.freq === 'monthly') s = n === 1 ? 'Monthly' : `Every ${n} months`;
  else s = n === 1 ? 'Annually' : `Every ${n} years`;

  const e = r.end || { type: 'never' };
  if (e.type === 'after') s += ` · ${e.count} time${e.count === 1 ? '' : 's'}`;
  else if (e.type === 'on' && e.date) s += ` · until ${e.date}`;
  return s;
}

// Returns an array of 'YYYY-MM-DD' start dates (always includes startISO itself
// unless a weekly byDay set excludes it).
export function expandRule(startISO, rule, opts = {}) {
  const hardCap = opts.cap || MAX_OCCURRENCES;
  if (!rule || rule.freq === 'none') return [startISO];

  const start = new Date(startISO + 'T00:00:00Z');
  if (isNaN(start)) return [startISO];
  const interval = Math.min(99, Math.max(1, rule.interval || 1));
  const end = rule.end || { type: 'never' };
  const limitCount = end.type === 'after' ? Math.min(hardCap, Math.max(1, end.count || 1)) : hardCap;
  const limitDate = end.type === 'on' && end.date ? end.date : null;
  // A "never" rule still has to stop somewhere: two years out.
  const horizon = iso(at(start.getUTCFullYear() + 2, start.getUTCMonth(), start.getUTCDate()));

  const out = [];
  const push = (dISO) => {
    if (limitDate && dISO > limitDate) return false;
    if (dISO > horizon) return false;
    out.push(dISO);
    return out.length < limitCount;
  };

  if (rule.freq === 'daily') {
    let cur = new Date(start);
    while (true) {
      if (!push(iso(cur))) break;
      cur.setUTCDate(cur.getUTCDate() + interval);
    }
  } else if (rule.freq === 'weekly') {
    const byDay = (rule.byDay && rule.byDay.length) ? rule.byDay.slice().sort() : [start.getUTCDay()];
    // Walk week by week from the Sunday of the start week.
    const weekStart = new Date(start);
    weekStart.setUTCDate(weekStart.getUTCDate() - weekStart.getUTCDay());
    let guard = 0;
    outer: while (guard++ < 520) {
      for (const dow of byDay) {
        const d = new Date(weekStart);
        d.setUTCDate(d.getUTCDate() + dow);
        const s = iso(d);
        if (s < startISO) continue;          // don't emit before the start date
        if (!push(s)) break outer;
      }
      weekStart.setUTCDate(weekStart.getUTCDate() + 7 * interval);
    }
  } else if (rule.freq === 'monthly') {
    const dayOfMonth = start.getUTCDate();
    const weekIndex = Math.floor((dayOfMonth - 1) / 7); // 0-based nth weekday
    const dow = start.getUTCDay();
    let y = start.getUTCFullYear(), m = start.getUTCMonth(), guard = 0;
    while (guard++ < 400) {
      let d;
      if (rule.monthMode === 'weekday') {
        const first = at(y, m, 1);
        const shift = (dow - first.getUTCDay() + 7) % 7;
        const day = 1 + shift + weekIndex * 7;
        d = day <= daysInMonth(y, m) ? at(y, m, day) : null;
      } else {
        d = dayOfMonth <= daysInMonth(y, m) ? at(y, m, dayOfMonth) : null; // skip short months
      }
      if (d && iso(d) >= startISO) { if (!push(iso(d))) break; }
      m += interval; y += Math.floor(m / 12); m %= 12;
    }
  } else { // yearly
    let y = start.getUTCFullYear(), guard = 0;
    const m = start.getUTCMonth(), dayOfMonth = start.getUTCDate();
    while (guard++ < 200) {
      if (dayOfMonth <= daysInMonth(y, m)) { if (!push(iso(at(y, m, dayOfMonth)))) break; }
      y += interval;
    }
  }
  return out;
}
